import {
  CartesianGrid,
  Label,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { TimeChartProps } from './types';
import type { EmotionTheme } from '../../../theme/emotionTheme';
import styled from '@emotion/styled';
import { TimeChartTooltip } from './TimeChartTooltip';
import { computeYAxisTicks, computeYDomainWithPadding } from './utils';

const MARGIN_BOTTOM_PX = 20;
const MARGIN_LEFT_PX = 10;

const TimeChartStyle = styled('div')<{ theme?: EmotionTheme }>`
  width: 100%;
  height: 100%;

  .chart-label {
    /* The package's own body stack. Was CircularXX, Arial, sans-serif — the one
     * Arial fallback in the app, and the only chart text that would have
     * dropped to a different face than everything around it. */
    font-family: var(--chakra-fonts-body);
    font-size: var(--chakra-font-sizes-xs);
  }

  .chart-tick {
    fill: ${(p) => p.theme.colors.muted['500']};
    font-size: 12px;
  }

  // Only show 3 ticks: at the bottom, at the top, and in the middle.
  .recharts-cartesian-grid-horizontal line {
    display: none;

    &:nth-child(1),
    &:nth-child(2),
    &:nth-child(3) {
      display: unset;
    }
  }
`;

export const TimeChart = ({ config, data }: TimeChartProps) => {
  const { xAxis, yAxis } = config;
  const yAxisTicks = computeYAxisTicks(yAxis.domain);
  const yAxisDomain = computeYDomainWithPadding(yAxis.domain);

  return (
    <TimeChartStyle>
      <ResponsiveContainer>
        <LineChart
          margin={{
            bottom: MARGIN_BOTTOM_PX,
            left: MARGIN_LEFT_PX,
          }}
          data={data}
        >
          <CartesianGrid strokeDasharray={'3'} vertical={false} />

          <XAxis
            dataKey={xAxis.dataKey}
            stroke={'0'}
            tickMargin={10}
            tick={(props) => {
              const {
                payload: { value },
                x,
                y,
              } = props;

              return (
                // We do "-5" to center the numbers as the custom tick
                // font is smaller than the original one
                <text className={'chart-tick'} y={y} x={Number(x) - 5}>
                  {value}
                </text>
              );
            }}
          >
            <Label
              className={'chart-label'}
              position="insideBottomLeft"
              style={{
                transform: `translate(-${MARGIN_LEFT_PX}px, ${MARGIN_LEFT_PX}px)`,
              }}
              value="DAYS"
            />
          </XAxis>

          <YAxis
            allowDataOverflow
            domain={yAxisDomain}
            orientation={'right'}
            stroke={'0'}
            ticks={yAxisTicks}
            tick={(props) => {
              const {
                payload: { value },
                x,
                y,
              } = props;

              if (value === yAxisTicks[0]) {
                return (
                  <text className={'chart-tick'} y={y} x={x}>
                    L
                  </text>
                );
              }

              if (value === yAxisTicks[1]) {
                return (
                  <text className={'chart-tick'} y={Number(y) + 4} x={x}>
                    M
                  </text>
                );
              }

              if (value === yAxisTicks[2]) {
                return (
                  <text className={'chart-tick'} y={Number(y) + 4} x={x}>
                    H
                  </text>
                );
              }

              return <></>;
            }}
          />

          <Tooltip animationDuration={150} content={<TimeChartTooltip />} />

          {yAxis.lines?.map(
            (line) =>
              line.visible && (
                <Line
                  type="monotone"
                  dataKey={line.dataKey}
                  stroke={line.color}
                  activeDot={{ r: 6 }}
                />
              )
          )}
        </LineChart>
      </ResponsiveContainer>
    </TimeChartStyle>
  );
};
